import React, { useEffect, useRef, useState, useCallback } from "react";
import api, { formatINR } from "../lib/api";
import { Input } from "./ui/input";
import { Search, Package, Tag, Loader2 } from "lucide-react";

/**
 * Searchable autocomplete picker that suggests products + price-list rows together.
 *
 *  <ItemSearchPicker
 *      onPick={(item) => updateRow({ name, size_label, hsn, rate, sku })}
 *      preferDistributorPrice           // optional; default false (uses MRP)
 *      placeholder="Search medicine…"
 *  />
 *
 * Each suggestion shows:
 *   • Medicine name + size label
 *   • Price (distributor or MRP, depending on preferDistributorPrice prop)
 *   • Source badge (Catalog / Price list / Catalog+List)
 *   • Stock badge (only for catalog items with stock value)
 */
export default function ItemSearchPicker({
    onPick,
    preferDistributorPrice = false,
    placeholder = "Search medicine, size, SKU…",
    testId = "item-search",
    autoFocus = false,
    initialQuery = "",
    minLength = 2,
}) {
    const [q, setQ] = useState(initialQuery);
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [highlight, setHighlight] = useState(0);
    const wrapRef = useRef(null);
    const debounceRef = useRef(null);

    // close on click outside
    useEffect(() => {
        const handler = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const search = useCallback(async (term) => {
        setLoading(true);
        try {
            const { data } = await api.get("/items/search", { params: { q: term, limit: 30 } });
            setItems(data || []);
            setOpen(true);
            setHighlight(0);
        } catch (e) {
            setItems([]);
        } finally { setLoading(false); }
    }, []);

    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        if (q.length < minLength) {
            setItems([]); setOpen(false); return;
        }
        debounceRef.current = setTimeout(() => search(q), 250);
        return () => debounceRef.current && clearTimeout(debounceRef.current);
    }, [q, minLength, search]);

    const pickItem = (item) => {
        const rate = preferDistributorPrice && item.distributor_price != null
            ? item.distributor_price
            : (item.mrp != null ? item.mrp : (item.distributor_price ?? 0));
        onPick({
            name: item.name,
            size_label: item.size_label,
            sku: item.sku,
            hsn: item.hsn || "",
            rate: rate || 0,
            product_id: item.product_id || null,
            size_id: item.size_id || null,
            stock: item.stock,
            source: item.source,
        });
        setQ("");
        setItems([]);
        setOpen(false);
    };

    const onKeyDown = (e) => {
        if (!open || items.length === 0) return;
        if (e.key === "ArrowDown") { e.preventDefault(); setHighlight((h) => Math.min(items.length - 1, h + 1)); }
        else if (e.key === "ArrowUp") { e.preventDefault(); setHighlight((h) => Math.max(0, h - 1)); }
        else if (e.key === "Enter") { e.preventDefault(); pickItem(items[highlight]); }
        else if (e.key === "Escape") { setOpen(false); }
    };

    return (
        <div ref={wrapRef} className="relative w-full" data-testid={testId}>
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onFocus={() => q.length >= minLength && setOpen(true)}
                onKeyDown={onKeyDown}
                placeholder={placeholder}
                autoFocus={autoFocus}
                className="rounded-none border-stone-300 pl-9"
                data-testid={`${testId}-input`}
            />
            {loading && <Loader2 className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 animate-spin" />}

            {open && (
                <div className="absolute left-0 right-0 top-full mt-1 z-50 bg-white border border-stone-300 shadow-lg max-h-80 overflow-y-auto" data-testid={`${testId}-results`}>
                    {items.length === 0 ? (
                        <div className="p-4 text-center text-xs text-stone-500">
                            {q.length < minLength ? `Type ${minLength}+ characters…` : "No matches."}
                        </div>
                    ) : items.map((it, idx) => {
                        const isHi = idx === highlight;
                        const price = preferDistributorPrice && it.distributor_price != null
                            ? it.distributor_price
                            : (it.mrp != null ? it.mrp : it.distributor_price);
                        const inStock = it.stock != null;
                        return (
                            <button
                                key={`${it.source}-${it.product_id || it.row_id || idx}-${it.size_label}`}
                                onMouseDown={(e) => { e.preventDefault(); pickItem(it); }}
                                onMouseEnter={() => setHighlight(idx)}
                                className={`w-full text-left p-2.5 border-b border-stone-100 flex items-center gap-3 text-sm transition-colors ${isHi ? "bg-sky-50" : "hover:bg-sky-50/50"}`}
                                data-testid={`${testId}-item-${idx}`}
                            >
                                <div className="flex-1 min-w-0">
                                    <div className="font-medium text-slate-900 truncate">
                                        {it.name} <span className="font-mono text-stone-500">{it.size_label}</span>
                                    </div>
                                    <div className="text-[11px] text-stone-500 flex items-center gap-2 mt-0.5">
                                        {it.sku && <span className="font-mono">{it.sku}</span>}
                                        {it.source === "price_list"
                                            ? <span className="inline-flex items-center gap-0.5 text-amber-700"><Tag className="w-2.5 h-2.5" />Price list</span>
                                            : it.source === "catalog+price_list"
                                                ? <span className="inline-flex items-center gap-0.5 text-emerald-700"><Tag className="w-2.5 h-2.5" />Catalog + List</span>
                                                : <span className="inline-flex items-center gap-0.5 text-sky-700"><Package className="w-2.5 h-2.5" />Catalog</span>}
                                        {inStock && it.stock > 0 && <span className="text-emerald-700">{it.stock} in stock</span>}
                                        {inStock && it.stock === 0 && <span className="text-red-700">Out of stock</span>}
                                        {it.notes && <span className="italic text-stone-500 truncate">· {it.notes}</span>}
                                    </div>
                                </div>
                                <div className="font-medium text-sky-700 whitespace-nowrap">
                                    {price != null ? formatINR(price) : "—"}
                                </div>
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
